import { useCallback, useEffect, useMemo } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { createSelector } from "reselect";

import TableContainer from "Common/Tables/StringsFiltersTable/TableContainer";
import ActionButton from "components/Buttons/ActionButton";
import { EditButton } from "components/Buttons/EditButton";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import StatusToggle from "components/StatusToggle";
import usePermission from "CustomHooks/usePermission";
import { app_modules } from "helpers/apps_modules_list";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";
import {
  fetchUser as onfetchUser,
  updateStatusInUsers,
  UsersxportsCSV,
} from "../../../slices/users/thunk";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";

//*// Redux store selector Memoization function
const reduxStoreUsers = createSelector(
  (state: any) => state.users,
  (Users: any) => ({
    usersList: Users.usersList,
    rowCount: Users.rowCount,
    loading: Users.loading,
    error: Users.error,
  })
);

const UserList = () => {
  document.title = "User List";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();

  // Use the preserved table state hook
  const tableState = usePreservedTableState({
    key: "user",
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue: colValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue: setColValue,
    resetState,
  } = tableState;

  const { usersList, rowCount, loading, error } = useSelector(reduxStoreUsers);

  // Use custom hook to fetch and access permissions
  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.user_management?.app_name,
    app_modules?.user_management?.modules?.user
  );

  // Get current user for status toggle check
  const user = useSelector((state: any) => state.Login.user);

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  // Convert filterValue (filterField/filterValue) to ColumnFiltersState (id/value) format
  const convertedFilters = useMemo(() => {
    if (!colValue || !Array.isArray(colValue)) return [];
    return colValue.map((f: any) => ({
      id: f.filterField || f.id,
      value: f.filterValue !== undefined ? f.filterValue : f.value,
    }));
  }, [colValue]);

  // Debounce search input to actual search
  useEffect(() => {
    // Skip if searchInput already matches search (prevents unnecessary updates during restoration)
    if (searchInput === search) {
      return;
    }

    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch, search]);

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  // Fetch data when search, filters, sorting, etc. change
  useEffect(() => {
    dispatch(
      onfetchUser({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: convertedFilters,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    convertedFilters,
  ]);

  const handleStatusToggle = async ({
    id,
    is_active,
  }: {
    id: string;
    is_active: boolean;
  }) => {
    // Prevent user from changing their own status
    if (id === user?.user_id?.toString()) {
      toast.error("You cannot change your own status.");
      return;
    }
    try {
      await dispatch(updateStatusInUsers({ id, is_active })).unwrap();
      toast.success(
        `User ${is_active ? "activated" : "deactivated"} successfully`
      );
      // Refresh the list
      dispatch(
        onfetchUser({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: convertedFilters,
        })
      );
    } catch (err: any) {
      console.error(err);
      toast.error("Something went wrong while updating status");
    }
  };

  // Handle CSV export
  const handleExport = async () => {
    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    let url: string | null = null; // declare here so it's accessible in finally
    try {
      const { blob, filename } = await UsersxportsCSV({
        search,
        filters: convertedFilters,
      });

      url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Export completed successfully.",
      });
    } catch (error: any) {
      console.error("Export failed", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to export CSV";

      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: errorMessage,
      });
    } finally {
      if (url) {
        window.URL.revokeObjectURL(url); // safely clean up if created
      }
    }
  };

  const handleEdit = useCallback(
    (data: any) => {
      // Navigate with user_id as URL param
      navigate(`/user-form/${data?.user_id}`);
    },
    [navigate]
  );

  //*// Dynamic Action column based on permissions
  const DynamicAction = useMemo(
    () =>
      canUpdate
        ? [
            {
              header: "Action",
              enableColumnFilter: false,
              enableSorting: false,
              cell: ({ row }: { row: any }) => (
                <ul className="d-flex gap-2 list-unstyled mb-0">
                  <li>
                    <EditButton onClick={() => handleEdit(row?.original)} />
                  </li>
                </ul>
              ),
            },
          ]
        : [],
    [canUpdate, handleEdit]
  );

  //*// Table columns (some are from reusable function)
  const columns = useMemo(
    () => [
      {
        header: "Sr. No.",
        accessorKey: "sr_no",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: { row: any }) => {
          return row.original.sr_no || row.index + 1 + pageIndex * pageSize;
        },
      },
      {
        header: "First Name",
        accessorKey: "first_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterFetchUrl: "/users/dropdown/first_name",
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => {
          return row.original.first_name || "-";
        },
      },
      {
        header: "Last Name",
        accessorKey: "last_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterFetchUrl: "/users/dropdown/last_name",
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => {
          return row.original.last_name || "-";
        },
      },
      {
        header: "User Name",
        accessorKey: "username",
        enableSorting: true,
        enableColumnFilter: true,
        filterFetchUrl: "/users/dropdown/username",
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => {
          return row.original.username || "-";
        },
      },
      {
        header: "Designation",
        accessorKey: "designation",
        enableSorting: true,
        enableColumnFilter: true,
        filterFetchUrl: "/users/dropdown/designation",
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => {
          return row.original.designation || "-";
        },
      },
      {
        header: "User Type",
        accessorKey: "user_type",
        enableSorting: true,
        enableColumnFilter: true,
        filterFetchUrl: "/users/dropdown/user_type",
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => {
          return row.original.user_type?.user_type ?? "-";
        },
      },
      {
        header: "Email",
        accessorKey: "email",
        enableSorting: true,
        enableColumnFilter: true,
        filterFetchUrl: "/users/dropdown/email",
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => {
          return row.original.email || "-";
        },
      },
      {
        header: "Mobile No",
        accessorKey: "mobile_number",
        enableSorting: true,
        enableColumnFilter: true,
        filterFetchUrl: "/users/dropdown/mobile_number",
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => {
          return row.original.mobile_number || "-";
        },
      },
      {
        header: "Status",
        accessorKey: "is_active",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          const userData = row.original;
          const isCurrentUser =
            userData.user_id?.toString() === user?.user_id?.toString();
          const superAdmin = userData.username?.toLowerCase() === "admin";

          return (
            <StatusToggle
              isActive={userData.is_active}
              onChange={() =>
                handleStatusToggle({
                  id: userData.user_id?.toString() || "",
                  is_active: !userData.is_active,
                })
              }
              disabled={!canUpdate || isCurrentUser || superAdmin}
            />
          );
        },
      },
      // {
      //   header: "Team",
      //   accessorKey: "team_name",
      //   enableSorting: true,
      //   enableColumnFilter: true,
      //   filterFetchUrl: "/users/dropdown/team_name",
      //   filterType: "dropdown" as const,
      //   cell: ({ row }: { row: any }) => {
      //     return row.original.team?.team_name ?? "N/A";
      //   },
      // },
      // {
      //   header: "Department",
      //   accessorKey: "department_name",
      //   enableSorting: true,
      //   enableColumnFilter: true,
      //   filterFetchUrl: "/users/dropdown/department_name",
      //   filterType: "dropdown" as const,
      //   cell: ({ row }: { row: any }) => {
      //     return row.original.department?.department_name ?? "N/A";
      //   },
      // },
      // {
      //   header: "Organisation",
      //   accessorKey: "organization_name",
      //   enableSorting: true,
      //   enableColumnFilter: true,
      //   filterFetchUrl: "/users/dropdown/organization_name",
      //   filterType: "dropdown" as const,
      //   cell: ({ row }: { row: any }) => {
      //     return row.original.organization?.organization_name ?? "N/A";
      //   },
      // },
      {
        header: "Address",
        accessorKey: "address",
        enableSorting: false,
        enableColumnFilter: false,
        maxSize: 250,
      },
      {
        header: "City",
        accessorKey: "city",
        enableSorting: true,
        enableColumnFilter: true,
        filterFetchUrl: "/users/dropdown/city",
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => {
          return row.original.city || "-";
        },
      },
      {
        header: "State",
        accessorKey: "state",
        enableSorting: true,
        enableColumnFilter: true,
        filterFetchUrl: "/users/dropdown/state",
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => {
          return row.original.state || "-";
        },
      },
      {
        header: "Country",
        accessorKey: "country",
        enableSorting: true,
        enableColumnFilter: true,
        filterFetchUrl: "/users/dropdown/country",
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => {
          return row.original.country || "-";
        },
      },
      {
        header: "Pincode",
        accessorKey: "pincode",
        enableSorting: true,
        enableColumnFilter: true,
        filterFetchUrl: "/users/dropdown/pincode",
        filterType: "dropdown" as const,
        cell: ({ row }: { row: any }) => {
          return row.original.pincode || "-";
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_user?.username ?? "N/A";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "N/A";
          return `${name} - ${date}`;
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_user?.username ?? "N/A";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "N/A";
          return `${name} - ${date}`;
        },
      },
      ...DynamicAction,
    ],
    [handleStatusToggle, canUpdate, DynamicAction, pageIndex, pageSize, user]
  );

  return (
    <ListingPageContainer>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">Users</h6>
                </Col>

                {/* Search Input Box (reusable)*/}
                <Col xl={2} md={3}>
                  <SearchBox value={searchInput} onChange={setSearchInput} />
                </Col>

                <Col className="col-md-auto">
                  <div className="hstack gap-2">
                    {canView && (
                      <ActionButton
                        label="Export"
                        icon="bi bi-box-arrow-up"
                        handleClick={handleExport}
                        variant="light"
                      />
                    )}
                    {canCreate && (
                      <ActionButton
                        label="Create User"
                        icon="bi bi-plus-circle"
                        navigateLink="/user-form"
                        variant="primary"
                      />
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Header>

            <Card.Body className="mt-3">
              <LoaderErrorWrapper loading={loading} error={error}>
                <TableContainer
                  columns={columns}
                  data={usersList && usersList.length > 0 ? usersList : []}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  colValue={setColValue}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  manualSorting={true}
                  isBordered={false}
                  isPagination={true}
                  isGlobalFilter={false}
                  SearchPlaceholder="Search user..."
                />
              </LoaderErrorWrapper>
              {/* )} */}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default UserList;
